'use client'
import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { RenderTabbedForm } from 'src/components/era-package/forms/RenderTabbedForm'
import { Interceptor } from './Interceptor'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import {
  useCargoEntityForm,
  useConfirmationForm,
  useHeaderForm,
  useLineItemForm
} from './tabs'
import { StatusResponse } from '../StatusResponse'
import pathMap from 'src/paths/pathMap'
import AbiEntrySummaryApiUrls from 'src/apiUrls/AbiEntrySummaryApiUrls'
import SnackBar from 'src/components/era-package/era/SnackBar'
import AbiUserSettingsApiUrls from 'src/apiUrls/AbiUserSettingsApiUrls'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelopes } from '@fortawesome/pro-light-svg-icons'

const Wrapper = ({ readOnly, id, children }) => {
  const [responseDialogOpen, setResponseDialogOpen] = useState(false)

  if (readOnly) return <>{children}</>
  return (
    <ParentCard title='Entry Summary Details' action={
      <Button variant='outlined'
        startIcon={<FontAwesomeIcon icon={faEnvelopes} />}
        onClick={() => setResponseDialogOpen(true)}
      >
        View Responses
      </Button>
    }>
      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>Note</AlertTitle>
        This form captures all required data elements for CBP Form 7501. Fields
        marked with{' '}
        <Box component='span' color='error.main'>
          *
        </Box>{' '}
        are mandatory.
      </Alert>
      {children}

      <Dialog open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        fullWidth maxWidth='lg'
      >
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <StatusResponse id={id} basic />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}
            sx={{ minWidth: 160 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </ParentCard>
  )
}

const EditEntrySummary = ({ isEntrySummary, readOnly, id }) => {
  const history = useHistory()
  const [isFetching, setFetching] = useState(false)
  const [fetchedData, setFetchedData] = useState()
  const [lineData, setLineData] = useState()
  const headerProps = useHeaderForm(id, fetchedData?.header)
  const entityProps = useCargoEntityForm(id, fetchedData?.cargo)
  const lineItemProps = useLineItemForm(id, lineData, setLineData)
  const confirmationProps = useConfirmationForm(id, fetchedData?.confirmation)

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
  const [showSubmitErrorSnackbar, setShowSubmitErrorSnackbar] = useState(false)

  const fetchUserSetting = async () => {
    try {
      const response = await fetch(`${AbiUserSettingsApiUrls.GET}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      })

      if (!response.ok) {
        console.log('Error fetching user setting')
        return { contactName: '', contactPhone: '' }
      }

      const json = await response.json()
      return json.data
    } catch (error) {
      console.log('Error fetching user seting')
      return { contactName: '', contactPhone: '' }
    }
  }

  // TODO set defaultValues combined, async
  useEffect(() => {
    let isAlive = true

    fetch(AbiEntrySummaryApiUrls.GET + `?abiTransId=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((json) => {
        if (isAlive && !!json.data) {
          let parse = json.data
          parse.cargo = parse.cargo.reduce(
            (acc, { id, partyCode, ...party }) => {
              Object.entries(party).forEach(
                ([fieldName, value]) =>
                  (acc[`${partyCode}-${fieldName}`] = value)
              )
              return acc
            },
            {}
          )
          fetchUserSetting().then((r) => {
            parse.header.contactName = r.contactName
            parse.header.contactPhone = r.contactPhone
            setFetchedData(parse)
            setLineData(parse.line)
            setFetching(true)
          })
        }
      })
      .catch((err) => {
        console.error(err)
        setFetching(true)
      })

    return () => {
      isAlive = false
      setFetching(true)
    }
  }, [id])

  // All the tabs
  const tabs = [
    ...(readOnly
      ? [
        {
          label: 'Status',
          render: () => <StatusResponse basic id={id} />
        }
      ]
      : []),
    {
      label: 'Header',
      key: 'header',
      formProps: headerProps
    },
    {
      label: 'Cargo Release Party',
      key: 'cargo',
      formProps: entityProps
    },
    {
      label: 'Line Items',
      key: 'line',
      formProps: lineItemProps
    },
    {
      label: 'Confirmation & Summary',
      key: 'confirmation',
      formProps: confirmationProps
    }
  ]

  const handleSubmit = async (formData) => {
    try {
      formData.cargo = convertCargoDatatoDTOForm(formData.cargo)
      formData.line = convertLineDatatoDTOForm(formData.line)

      const response = await fetch(AbiEntrySummaryApiUrls.SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(formData) // Convert JS object to JSON string
      })

      if (!response.ok) {
        console.error('Error fetching user data')
        setShowSubmitErrorSnackbar(true)
        return false
      }

      const result = await response.json()
      setShowSuccessSnackbar(formData.header.abiTransId)
      console.log('submitted:', result)
      return true
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  function convertCargoDatatoDTOForm(input) {
    // UI to DTO form
    const grouped = {}

    for (const [key, value] of Object.entries(input)) {
      if (!key.includes('-')) continue // Skip fields without party code

      const [partyCode, field] = key.split('-')
      if (!grouped[partyCode]) {
        grouped[partyCode] = {
          partyLookup: partyCode,
          partyCode: partyCode
        }
      }
      grouped[partyCode][field] = value
    }

    return Object.values(grouped)
  }

  function convertLineDatatoDTOForm(input) {
    if (input?.items && Array.isArray(input.items)) {
      return input.items
    }
    return input
  }

  // Handle snackbar close
  const handleSuccessSnackbarClose = (e, reason) => {
    if (reason === 'timeout') {
      history.push(
        pathMap.ABI_ENTRY_SUMMARY_RESPONSE_VIEW.replace(
          ':id',
          showSuccessSnackbar
        )
      )
      setShowSuccessSnackbar(false)
    }
  }

  // Handle snackbar close
  const handleSubmitErrorSnackbarClose = useCallback(() => {
    setShowSubmitErrorSnackbar(false)
  }, [])

  if (!isFetching) return <p>Loading user data...</p>

  return (
    <Wrapper readOnly={readOnly} id={id}>
      <RenderTabbedForm
        tabs={tabs}
        asyncDefaultValues={fetchedData}
        onSubmit={handleSubmit}
        readOnly={readOnly}
        isEntrySummary={isEntrySummary}
      >
        <Interceptor asyncDefaultValues={fetchedData} />
      </RenderTabbedForm>
      {/* Error Snackbar - Only show when showErrorSnackbar is true */}
      <SnackBar
        title='Form Submittion Error'
        type='error'
        open={showSubmitErrorSnackbar}
        onClose={handleSubmitErrorSnackbarClose}
        duration={10000}
      >
        Internal error occured, please try again later.
      </SnackBar>

      {/* Success Snackbar - Only show when showSuccessSnackbar is true */}
      <SnackBar
        title='Form submitted successfully'
        type='success'
        open={!!showSuccessSnackbar}
        onClose={handleSuccessSnackbarClose}
        duration={3000}
      >
        Redirecting to Response Summary...
      </SnackBar>
    </Wrapper>
  )
}

export default EditEntrySummary
